import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../global/supabase';
import { CLIENT_ID, BRANDING, LOCATIONS, MEDICATIONS, LOCATION_MEDICATIONS } from '../config';

// Default templates matching seeded values (for reset)
const DEFAULTS = {
  daily: {
    system_role: `You are an AI compliance analyst for Elsmere Fire Protection District's controlled substance inventory management system (NarcoticTrack). Your role is to analyze the last 24 hours of narcotic inventory logs across all units and generate a concise, actionable compliance report. You focus on identifying discrepancies, missing logs, policy violations, and patterns that require supervisor attention. You are direct, precise, and prioritize patient safety and DEA compliance.`,
    flag_criteria: `CRITICAL: Any unit with zero logs in 24h when active, medication quantity discrepancy without documented reason (such as Patient Use/Restock or Expired Removal), missing/null required fields, negative quantities
WARNING: Tag changes without documentation, units with only 1 log in 24h, quantities reaching zero, same personnel logging all entries (no cross-verification)
INFO: Notable personnel notes, run numbers logged, routine seal verifications, new personnel`,
    analysis_priorities: '',
    additional_instructions: '',
  },
  weekly: {
    system_role: `You are an AI compliance strategist for Elsmere Fire Protection District, generating a WEEKLY intelligence report for narcotic inventory and controlled substance management. Think like a Division Chief reviewing a week of operations. Focus on PATTERNS and TRENDS across the entire week, not individual daily incidents. Identify systemic issues, personnel patterns, compliance trajectory, and strategic recommendations. Your audience is senior leadership.`,
    flag_criteria: `CRITICAL: Any unit with zero logs in 24h when active, medication quantity discrepancy without documented reason (such as Patient Use/Restock or Expired Removal), missing/null required fields, negative quantities
WARNING: Tag changes without documentation, units with only 1 log in 24h, quantities reaching zero, same personnel logging all entries (no cross-verification)
INFO: Notable personnel notes, run numbers logged, routine seal verifications, new personnel`,
    analysis_priorities: `Compliance trends (improving/stable/declining)
Medication usage patterns (unusual consumption, zero-quantity events, restock anomalies)
Personnel accountability (coverage gaps, cross-verification)
Systemic issues (process/training gaps)
Daily flags follow-up (were critical flags addressed?)`,
    additional_instructions: '',
  },
};

function PromptTemplateEditor({ userInfo }) {
  const [activeTab, setActiveTab] = useState('daily');
  const [template, setTemplate] = useState(null);
  const [formData, setFormData] = useState({ system_role: '', flag_criteria: '', analysis_priorities: '', additional_instructions: '' });
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showGuide, setShowGuide] = useState(false);

  const hasChanges = originalData && JSON.stringify(formData) !== JSON.stringify(originalData);

  const loadTemplate = useCallback(async (type) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('ai_prompt_templates')
        .select('*')
        .eq('prompt_type', type)
        .eq('deployment_id', CLIENT_ID)
        .eq('is_active', true)
        .limit(1)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;

      if (data) {
        setTemplate(data);
        const fields = {
          system_role: data.system_role || '',
          flag_criteria: data.flag_criteria || '',
          analysis_priorities: data.analysis_priorities || '',
          additional_instructions: data.additional_instructions || '',
        };
        setFormData(fields);
        setOriginalData(fields);
      } else {
        // No template found, use defaults
        setTemplate(null);
        const defaults = DEFAULTS[type];
        setFormData({ ...defaults });
        setOriginalData({ ...defaults });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplate(activeTab); }, [activeTab, loadTemplate]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (template) {
        const { error: updateError } = await supabase
          .from('ai_prompt_templates')
          .update({
            system_role: formData.system_role,
            flag_criteria: formData.flag_criteria,
            analysis_priorities: formData.analysis_priorities || null,
            additional_instructions: formData.additional_instructions,
            updated_by: userInfo?.id || null,
          })
          .eq('id', template.id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('ai_prompt_templates')
          .insert({
            prompt_type: activeTab,
            name: 'Default',
            system_role: formData.system_role,
            flag_criteria: formData.flag_criteria,
            analysis_priorities: formData.analysis_priorities || null,
            additional_instructions: formData.additional_instructions,
            is_active: true,
            deployment_id: CLIENT_ID,
            created_by: userInfo?.id || null,
          });
        if (insertError) throw insertError;
      }
      setOriginalData({ ...formData });
      setSuccess('Template saved successfully.');
      setTimeout(() => setSuccess(null), 3000);
      await loadTemplate(activeTab);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const defaults = DEFAULTS[activeTab];
    setFormData({ ...defaults });
  };

  const handleDiscard = () => {
    if (originalData) setFormData({ ...originalData });
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const textareaStyle = {
    width: '100%',
    minHeight: '120px',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '13px',
    lineHeight: '1.5',
    resize: 'vertical',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '6px',
    fontWeight: '600',
    color: '#333',
    fontSize: '14px',
  };

  // Gather all medication names for guide
  const allMeds = [...MEDICATIONS.map(m => `${m.id} → ${m.displayName}`)];
  for (const [, locConfig] of Object.entries(LOCATION_MEDICATIONS)) {
    for (const section of locConfig.sections) {
      for (const med of section.medications) {
        allMeds.push(`${med.id} → ${section.title} ${med.displayName}`);
      }
    }
  }

  return (
    <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid #ddd', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ background: BRANDING.primaryColor, color: '#fff', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '16px' }}>AI Prompt Template Editor</h3>
        <button onClick={() => setShowGuide(!showGuide)} style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
          {showGuide ? 'Hide Guide' : 'Show Guide'}
        </button>
      </div>

      <div style={{ display: 'flex' }}>
        {/* Main Editor */}
        <div style={{ flex: 1, padding: '16px 20px' }}>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: '0', marginBottom: '20px', borderBottom: '2px solid #eee' }}>
            {['daily', 'weekly'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '10px 20px',
                  background: 'none',
                  border: 'none',
                  borderBottom: activeTab === tab ? `3px solid ${BRANDING.primaryColor}` : '3px solid transparent',
                  color: activeTab === tab ? BRANDING.primaryColor : '#666',
                  fontWeight: activeTab === tab ? '600' : '400',
                  fontSize: '14px',
                  cursor: 'pointer',
                  marginBottom: '-2px',
                }}
              >
                {tab === 'daily' ? 'Daily Report' : 'Weekly Report'}
              </button>
            ))}
            {hasChanges && (
              <span style={{ marginLeft: 'auto', alignSelf: 'center', color: '#f57c00', fontSize: '12px', fontWeight: '600' }}>
                ● Unsaved changes
              </span>
            )}
          </div>

          {loading ? (
            <p style={{ textAlign: 'center', color: '#666' }}>Loading template...</p>
          ) : (
            <>
              {/* System Role */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>System Role</label>
                <textarea
                  value={formData.system_role}
                  onChange={e => handleFieldChange('system_role', e.target.value)}
                  style={{ ...textareaStyle, minHeight: '140px' }}
                  placeholder="Define the AI persona and behavior..."
                />
              </div>

              {/* Flag Criteria */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Flag Criteria</label>
                <textarea
                  value={formData.flag_criteria}
                  onChange={e => handleFieldChange('flag_criteria', e.target.value)}
                  style={textareaStyle}
                  placeholder="Define CRITICAL, WARNING, and INFO flag rules..."
                />
              </div>

              {/* Analysis Priorities (weekly only) */}
              {activeTab === 'weekly' && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Analysis Priorities</label>
                  <textarea
                    value={formData.analysis_priorities}
                    onChange={e => handleFieldChange('analysis_priorities', e.target.value)}
                    style={textareaStyle}
                    placeholder="Define weekly analysis focus areas..."
                  />
                </div>
              )}

              {/* Additional Instructions */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Additional Instructions</label>
                <textarea
                  value={formData.additional_instructions}
                  onChange={e => handleFieldChange('additional_instructions', e.target.value)}
                  style={{ ...textareaStyle, minHeight: '80px' }}
                  placeholder="Any deployment-specific customizations..."
                />
              </div>

              {/* Messages */}
              {error && <div style={{ padding: '10px', background: '#fce4ec', color: '#c62828', borderRadius: '4px', marginBottom: '12px', fontSize: '13px' }}>{error}</div>}
              {success && <div style={{ padding: '10px', background: '#e8f5e9', color: '#2e7d32', borderRadius: '4px', marginBottom: '12px', fontSize: '13px' }}>{success}</div>}

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button onClick={handleSave} disabled={saving || !hasChanges} style={{ padding: '10px 20px', background: hasChanges ? BRANDING.primaryColor : '#ccc', color: '#fff', border: 'none', borderRadius: '4px', cursor: hasChanges ? 'pointer' : 'default', fontSize: '13px', fontWeight: '600' }}>
                  {saving ? 'Saving...' : 'Save Template'}
                </button>
                <button onClick={handleReset} style={{ padding: '10px 20px', background: '#f5f5f5', color: '#333', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                  Reset to Defaults
                </button>
                {hasChanges && (
                  <button onClick={handleDiscard} style={{ padding: '10px 20px', background: '#fff', color: '#d32f2f', border: '1px solid #d32f2f', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                    Discard Changes
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Side Guide Panel */}
        {showGuide && (
          <div style={{ width: '260px', borderLeft: '1px solid #eee', padding: '16px', background: '#fafafa', fontSize: '12px', overflowY: 'auto', maxHeight: '600px' }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#333' }}>Reference Guide</h4>

            <div style={{ marginBottom: '16px' }}>
              <strong style={{ color: BRANDING.primaryColor }}>Units</strong>
              {LOCATIONS.map(loc => (
                <div key={loc.id} style={{ padding: '3px 0', color: '#555' }}>
                  {loc.name} <span style={{ color: '#999' }}>({loc.type})</span>
                </div>
              ))}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <strong style={{ color: BRANDING.primaryColor }}>Medications</strong>
              {allMeds.map((m, i) => (
                <div key={i} style={{ padding: '2px 0', color: '#555', fontFamily: 'monospace', fontSize: '11px' }}>{m}</div>
              ))}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <strong style={{ color: BRANDING.primaryColor }}>Severity Levels</strong>
              <div style={{ padding: '2px 0' }}><span style={{ color: '#d32f2f' }}>●</span> critical</div>
              <div style={{ padding: '2px 0' }}><span style={{ color: '#f57c00' }}>●</span> warning</div>
              <div style={{ padding: '2px 0' }}><span style={{ color: '#1976d2' }}>●</span> info</div>
            </div>

            <div>
              <strong style={{ color: BRANDING.primaryColor }}>Status Values</strong>
              <div style={{ padding: '2px 0' }}><span style={{ color: '#2e7d32' }}>●</span> compliant</div>
              <div style={{ padding: '2px 0' }}><span style={{ color: '#f57c00' }}>●</span> needs_attention</div>
              <div style={{ padding: '2px 0' }}><span style={{ color: '#d32f2f' }}>●</span> non_compliant</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PromptTemplateEditor;
